import { createContext, useCallback, useContext, useRef, useState } from 'react';

export type NotificationType = 'error' | 'info' | 'success';

interface Notification {
  id: number;
  type: NotificationType;
  title: string;
  message: string;
  duration: number;
}

export interface NotificationsContextType {
  notifications: Notification[];
  dispatch(
    type: Notification['type'],
    title: Notification['title'],
    message: Notification['message'],
    duration?: Notification['duration'],
  ): void;
  closeNotification(id: Notification['id']): void;
}

const NotificationProviderContext = createContext<NotificationsContextType>({
  notifications: [],
  dispatch() {
    // no-op
  },
  closeNotification() {
    // no-op
  },
});

/**
 * We do not dispatch error messages found in this list
 */
const ignoredErrors = [
  'Store reset while query was in flight(not completed in link chain)',
  'Unexpected token P in JSON at position 0',
];

export interface NotificationProviderProps {
  children?: React.ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const idCounter = useRef(0);

  const dispatch = useCallback<NotificationsContextType['dispatch']>((type, title, message, duration = 5) => {
    if (ignoredErrors.includes(message)) {
      return;
    }

    setNotifications(curr => curr.concat({ id: idCounter.current++, type, title, message, duration }));
  }, []);

  const closeNotification = useCallback<NotificationsContextType['closeNotification']>(id => {
    setNotifications(curr => curr.filter(n => n.id !== id));
  }, []);

  return (
    <NotificationProviderContext.Provider value={{ notifications, dispatch, closeNotification }}>
      {children}
    </NotificationProviderContext.Provider>
  );
};

export function useNotificationContext() {
  return useContext(NotificationProviderContext);
}
